import type { Moment } from 'moment';
import { z } from 'zod';
import {
    WidgetType,
    ZodBaseWidgetConfiguration,
    ZodGenericWidgetConfigurationDetails,
} from '../types';
import type { RRuleSet } from 'rrule';
import type { ApiSuccessResponse } from '../../ReachApi';
import { Nullish } from '../../Utility/Nullish';
import {
    zEmptyStringAsNull,
    zMarshalToBoolean,
    zMarshalToStrictInt,
    zMarshalToStrictIntNullable,
} from '../../ReachApi/Marshallers';

export const ZodScheduleWidgetConfigurationDetails = ZodGenericWidgetConfigurationDetails.extend({
    css_header: z.nullable(z.string()),
    css_labels: z.nullable(z.string()),
    css_events: z.nullable(z.string()),
    facilityid: z.string(),
    screenid: z.nullable(z.string()), //If not null or empty string, then it is a legacy calendar
    additional_days: z
        .union([z.string(), z.number()])
        .nullish()
        .transform(zEmptyStringAsNull)
        .transform(zMarshalToStrictIntNullable),
    scroll_override: z.string().transform(zMarshalToBoolean),
    fade: z.string().transform(zMarshalToBoolean),
    format: z.ostring().nullable(), //Defaults to 'classic' on the backend
    css_time: z.ostring().nullable(),
    event_click: z.string().transform(zMarshalToBoolean),
    switch_announcements: z.string().transform(zMarshalToBoolean), //Seemingly unused
    ann_fac_id: z.ostring().nullable(), //Seemingly unused
    ann_screen_id: z.ostring().nullable(), //Seemingly unused
    scroll_speed: z.ostring().nullable(),
    touch_scroll: z.string().transform(zMarshalToBoolean),
    calendar_controls: z.string().transform(zMarshalToBoolean),
    local_timezone: z.string().transform(zMarshalToBoolean),
    show_expired_data: z.string().transform(zMarshalToBoolean),
    group_id: z.ostring().nullable(),
    calendar_id: z.ostring().nullable(),
    template_version: z.ostring().nullable(),
    show_rows: z
        .union([z.string(), z.number()])
        .nullish()
        .transform(zEmptyStringAsNull)
        .transform(zMarshalToStrictIntNullable),
});

export type ScheduleWidgetConfigurationDetails = z.infer<
    typeof ZodScheduleWidgetConfigurationDetails
>;

export const ZodScheduleWidgetConfiguration = ZodBaseWidgetConfiguration.extend({
    type: z.literal(WidgetType.SCHEDULE),
    config: ZodScheduleWidgetConfigurationDetails,
});

export type ScheduleWidgetConfiguration = z.infer<typeof ZodScheduleWidgetConfiguration>;

//todo: Rename types to provide more clarity on their purpose
//todo: Can we merge types that are similar?  Is there extra work being done that is not necessary?

export type CalendarChangeKeyRecord = {
    calendar_id: string; //uuid
    change_key: string; //uuid
};

/**
 * A type with all the information needed to display a schedule event
 *
 * The ultimate event list contains both ScheduleWidgetEvents and RecurrenceEvents. These are built
 * and parsed in very different ways, and both contain a lot more info than is needed to display
 * the event.
 */
export type ScheduleWidgetDisplayEvent = {
    calendarId: string;
    eventId: string; //uuid
    rrule: string | null;
    startDate: string;
    endDate: string;
    startDateMoment: Moment;
    endDateMoment: Moment;
    metadata: Record<string, string>; //Dynamically added calendar information
};

export type ScheduleWidgetEvent = ScheduleWidgetDisplayEvent & {
    rinkid: string;
    allDay: boolean;
    eventFirstOccurrence: string | null;
    eventLastOccurence: string | null; //[sic]
    exceptions: ScheduleExceptionEvent[] | null; // todo, confirm this is correct type
    localTimezone: string;
    rruleset: RRuleSet | null;
    duplicateEvent?: boolean;
    recurrences?: RecurrenceEvent[];
    parentEvent?: ScheduleWidgetEvent;
};

//todo: what is optional, what is not
export type ScheduleEventInfo = {
    infoid?: string;
    information?: string;
    sdate?: string; //Start Date
    stime?: string; //Start Time
    edate?: string; //End Date
    etime?: string; //End Time
    allday: 'true' | 'false';
    starttime: string;
    startDateMoment: Moment;
    endDateMoment: Moment;
    endtime: string;
    facilityid: string;
    location?: string;
    instructor?: string;
    descr?: string;
    event?: string;
    hometeam?: string | null;
    awayteam?: string | null;
    awaylocker?: string | null;
    homelocker?: string | null;
    rinkid?: string;
    rinkname?: string;
    rinktype: string;
    mainheadertext?: string;
    metadata: Record<string, string>; //Dynamically added calendar information

    [templateKey: string]: unknown; //Metadata properties are also added dynamically to ScheduleEventInfo
};

export type ScheduleInfo = {
    events: ScheduleEventInfo[];
    headerinfo: {
        date?: string;
        rinkid?: string;
        rinkname?: string;
        rinktype: string; //todo: make enum 'Rink' | 'Time-Event-MultiDay' | ...
        rinkname_date_info?: {
            date: number;
            day_of_week: string;
            month_name: string;
            month_number: number;
            number_day: number;
            short_month_name: string;
        };
        mainheadertext?: string;
        TZOFFSET: number | null; //this value doesn't seem to be used, but is still returned by the API
        autoupload: number;
        collapse: number;
        color1?: string;
        color2: string | null;
        fontsize: number;
        importformat: null; //this value doesn't seem to be used, but is still returned by the API
        numrows: number;
        screenformat: 'RINK';
        screenid: string | null;
        screenrefresh: number;
        scrollinfo: number;
        scrollspeed: string | Nullish;
        showLocker: number;
        showorder: number;
        calendar_id?: string;
    };
};

export type ScheduleExceptionEvent = ScheduleWidgetDisplayEvent & {
    eventRecurrenceId: string | null; //todo: Why is this sometimes null?
    eventRecurs: boolean;
    eventEffectiveId: string;
};

export interface RecurrenceEvent extends ScheduleExceptionEvent {
    duplicateEvent: boolean;
    parentEvent: ScheduleWidgetEvent;
}

export interface GetScheduleResponse extends ApiSuccessResponse {
    schedules: ScheduleInfo[];
}

export interface GetCalendarGroupsAPIResponse extends ApiSuccessResponse {
    groups: {
        id: number;
        name: string;
        calendars: Calendar[];
    };
}

export interface GetCalendarAPIResponse extends ApiSuccessResponse {
    calendar: Calendar;
}

export interface GetCalendarsAPIResponse extends ApiSuccessResponse {
    calendars: CalendarWithEvents[];
}

const ZodCalendarTemplateField = z.object({
    id: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    calendar_ui_display: z.unknown().transform(zMarshalToBoolean),
    name: z.string(),
    required: z.unknown().transform(zMarshalToBoolean),
    signage_property_name: z.string(),
    synthetic: z.union([z.literal('true'), z.literal('false'), z.null()]),
    synthetic_code: z.union([z.string(), z.null()]),
    type: z.object({
        id: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
        description: z.string(),
        type: z.string(),
    }),
});

const ZodCalendarTemplate = z.object({
    id: z.string(),
    long_form: z.unknown(), //Unclear what this property is used for
    short_form: z.unknown(), //Unclear what this property is used for
    name: z.string(),
    ownership: z.string(),
    default: z.unknown().transform(zMarshalToBoolean),
    template_category: z.string(), //todo: Gather all values and create enum
    fields: z.array(ZodCalendarTemplateField),
});

export type CalendarTemplate = z.infer<typeof ZodCalendarTemplate>;

const ZodCalendar = z.object({
    id: z.string(),
    calendar_id: z.string(), //uuid
    change_key: z.string(), //Change key uuid
    color: z.string(), //hex code
    created: z.string(), //UTC date time
    description: z.string(),
    last_modified: z.string(), //UTC date time
    local_timezone: z.string(),
    name: z.string(),
    status: z.string(), //calendar status
    template: ZodCalendarTemplate,
});

export type Calendar = z.infer<typeof ZodCalendar>;

const ZodRawExceptionEvent = z.object({
    event_recurrence_id: z.string(),
    eventRecurs: z.boolean().or(z.undefined()),
    calendar_id: z.string(),
    event_id: z.string(),
    rrule: z.string().nullish(),
    start_date: z.number(), //UNIX timestamp
    end_date: z.number(), //UNIX timestamp
    all_day: z.boolean(),
    metadata: z.record(z.string()), //Dynamically added calendar information
});
export type RawExceptionEvent = z.infer<typeof ZodRawExceptionEvent>;

/**
 * Events from API before they are parsed.
 */
const ZodRawEvent = z.object({
    all_day: z.boolean(),
    calendar_id: z.string(),
    created: z.number(),
    start_date: z.number(),
    end_date: z.number(),
    event_first_occurrence: z.number(), //Unix timestamp
    event_id: z.string(),
    event_last_occurrence: z.number().nullable(),
    exceptions: z.array(ZodRawExceptionEvent),
    last_modified: z.number(),
    metadata: z.record(z.string()),
    rrule: z.string().nullish(),
});

export type RawEvent = z.infer<typeof ZodRawEvent>;

export const ZodCalendarWithEvents = ZodCalendar.extend({
    events: z.array(ZodRawEvent),
    show_rows: z.number(), //number of rows to display
});

export type CalendarWithEvents = z.infer<typeof ZodCalendarWithEvents>;

/**
 * Parsed Events multi-dimensional dictionary(aka associative array)
 *
 * (1st dimension = year*, 2nd dimension = month*, 3rd dimension = day*; where
 * '*' is year, month or day number)
 *
 * @example
 * <caption>Retrieving the Events for Sept. 6th, 2016.</caption>
 * var events = parsedEvents['year2016']['month9']['day6'];
 */
export type ParsedEvents = Record<
    string,
    Record<string, Record<string, ScheduleWidgetDisplayEvent[]>>
>;

//todo: Use this in future version of TS
// type YearLiteral = `year${number}`;
// type MonthLiteral = `month${number}`;
// type DayLiteral = `day${number}`;
// export type ParsedEvents =
//      Record<YearLiteral, Record<MonthLiteral, Record<DayLiteral, ScheduleWidgetEvent[]>>>;

export type ScheduleWidgetTemplateRenderData = ScheduleWidgetHandlebarsRenderData &
    ScheduleWidgetVueRenderData & {
        schedule: ScheduleInfo[];
    };

export type newExceptionEvent = {
    eventRecurrenceId: string | null;
    eventRecurs: boolean;
    calendarId: string;
    eventId: string;
    rrule: string | null;
    startDate: number;
    endDate: number;
    metadata: Record<string, string>;
    allDay: boolean;
};

type ScheduleWidgetHandlebarsRenderData = {
    Widget: {
        Id: number;
    };
    wobj: {
        widget: {
            widgetsid: number;
        };
    };
    viewBag: {
        widgetid: number;
        widget_css_header: string | Nullish;
        widget_css_labels: string | Nullish;
        widget_css_events: string | Nullish;
        widget_css_times: string | Nullish;
        pixels_per_second: number;
    };
};

type ScheduleWidgetVueRenderData = {
    widget: {
        id: number;
    };
    metadata: {
        marquee: {
            active: boolean;
            speed: string;
            override: boolean;
            touchScroll: boolean;
            allowTouchScrolling: boolean;
            copy: number[]; //minimum number of copies required for continuous marquee effect
        };
    };
};

export enum CalendarQuarter {
    FIRST_QUARTER = 1,
    SECOND_QUARTER = 2,
    THIRD_QUARTER = 3,
    FOURTH_QUARTER = 4,
}
